import { useState } from "react";
import {
  FolderOpen,
  FolderPlus,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { librarySectionHeadingClassName } from "@/components/library/libraryNavStyles";
import {
  libraryContainerCardWrapClassName,
  libraryContainerGridClassName,
  LIBRARY_SINGLE_ROW_SEE_ALL_THRESHOLD,
} from "@/components/library/libraryCompactRowLayout";
import { DroppableLibraryFolder } from "@/components/library/LibraryDroppables";
import type { LibraryFolder } from "@/services/foldersService";

type LibraryFolderStripProps = {
  folders: LibraryFolder[];
  folderItemCounts: Map<string, number>;
  onFolderClick: (folderId: string) => void;
  onNewSubfolder: (parentId: string) => void;
  onEditFolder: (folder: LibraryFolder) => void;
  onDeleteFolder: (folder: LibraryFolder) => void;
};

export function LibraryFolderStrip({
  folders,
  folderItemCounts,
  onFolderClick,
  onNewSubfolder,
  onEditFolder,
  onDeleteFolder,
}: LibraryFolderStripProps) {
  const [showAll, setShowAll] = useState(false);

  if (folders.length === 0) return null;

  const hasMore = folders.length > LIBRARY_SINGLE_ROW_SEE_ALL_THRESHOLD;

  return (
    <section className="mb-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className={librarySectionHeadingClassName}>Folders</h2>
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs shrink-0"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "Show one row" : `See all (${folders.length})`}
          </Button>
        )}
      </div>
      <div className={libraryContainerGridClassName(showAll)}>
        {folders.map((folder) => {
          const itemCount = folderItemCounts.get(folder.id) || 0;
          return (
            <div key={folder.id} className={libraryContainerCardWrapClassName(showAll)}>
              <DroppableLibraryFolder folder={folder}>
                <Card
                  className="group hover:shadow-md transition-shadow cursor-pointer border-amber-200 bg-amber-50/50 dark:bg-amber-950/30 dark:border-amber-800 h-full"
                  onClick={() => onFolderClick(folder.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-1 mb-1.5">
                      <FolderOpen className="h-4 w-4 text-amber-600 shrink-0" />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onNewSubfolder(folder.id);
                            }}
                          >
                            <FolderPlus className="h-4 w-4 mr-2" />
                            New subfolder
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditFolder(folder);
                            }}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Rename folder
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteFolder(folder);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete folder
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <h3 className="font-medium text-sm line-clamp-2 leading-snug">{folder.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {itemCount} {itemCount === 1 ? "item" : "items"}
                    </p>
                  </CardContent>
                </Card>
              </DroppableLibraryFolder>
            </div>
          );
        })}
      </div>
    </section>
  );
}
